/**
 * Account name form component (APP-002 §8, §33).
 *
 * Renders the rename surface backed by `useAccountNameForm`. Accessible:
 * associated label, describedby error messaging, disabled while saving,
 * field-level + page-level errors, success confirmation, and cancel/reset.
 * Never permits editing account ID, plan, status, entitlement, quota, billing,
 * or Clerk identifiers — only `name` is sent (APP-002 §8).
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RequestId } from '@/components/feedback/RequestId';
import { Check } from '@/components/icons';
import { ACCOUNT_NAME_MAX, useAccountNameForm } from './useAccountNameForm';

export interface AccountNameFormProps {
  /** Current backend value, prefilled. */
  initialName: string;
  /** Heading level context; the form renders its own visible label. */
  label?: string;
  /** Optional CTA fired after a successful save (e.g. onboarding advance). */
  onSuccess?: () => void;
  /** Show a cancel affordance. */
  onCancel?: () => void;
  /** Submit button label override. */
  submitLabel?: string;
}

export function AccountNameForm({
  initialName,
  label = 'Account name',
  onSuccess,
  onCancel,
  submitLabel = 'Save name',
}: AccountNameFormProps) {
  const form = useAccountNameForm(initialName);

  const fieldError = form.clientError ?? form.serverFieldError;
  const errorId = 'account-name-error';
  const inputId = 'account-name-input';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const ok = await form.submit();
    if (ok && onSuccess) onSuccess();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>{label}</Label>
        <Input
          id={inputId}
          name="account-name"
          value={form.value}
          onChange={(e) => form.setValue(e.target.value)}
          maxLength={ACCOUNT_NAME_MAX}
          autoComplete="organization"
          disabled={form.isSubmitting}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={fieldError ? errorId : undefined}
        />
        {fieldError ? (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {fieldError}
          </p>
        ) : null}
        {/* Preserve the DIRECT-011 request ID for support even on field errors. */}
        {form.serverFieldError && form.requestId ? (
          <RequestId requestId={form.requestId} className="text-xs" />
        ) : null}
      </div>

      {form.serverError ? (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{form.serverError}</p>
            {form.requestId ? <RequestId requestId={form.requestId} className="mt-2" /> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {form.isSuccess ? (
        <p role="status" className="flex items-center gap-1.5 text-xs text-success">
          <Check className="h-3.5 w-3.5" />
          Account name updated.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={form.isSubmitting}>
          {form.isSubmitting ? 'Saving…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={form.isSubmitting}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
